import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/atoms/Button';
import ContactForm from '@/components/organisms/ContactForm';
import Navbar from '@/components/organisms/Navbar';
import { motion } from 'motion/react';
import { Typewriter } from '@/lib/motion-plus';
import {
  PowerIcon,
  AlertIcon,
  AIIcon,
  VisionIcon,
  SecurityIcon,
} from '@/components/atoms/FeatureIcons';

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  },
};

// Custom icons for this page
const AirgapIcon: FC<{ size?: number; className?: string }> = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 2" fill="currentColor" fillOpacity="0.1" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ShieldLockIcon: FC<{ size?: number; className?: string }> = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 3L4 7v5c0 5.25 3.4 10.15 8 11.25 4.6-1.1 8-6 8-11.25V7l-8-4z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.15" />
    <rect x="9" y="10" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 10V8a2 2 0 114 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LocalProcessingIcon: FC<{ size?: number; className?: string }> = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="4" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.15" />
    <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 8v4M10 10h4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

const NuclearIcon: FC<{ size?: number; className?: string }> = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <path d="M12 5c-2.5 3-2.5 5 0 7 2.5-2 2.5-4 0-7z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2" />
    <path d="M5.5 15c3.5 0 5-1.5 6.5-3-2.5 0-5 0-6.5 3z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2" />
    <path d="M18.5 15c-3.5 0-5-1.5-6.5-3 2.5 0 5 0 6.5 3z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2" />
  </svg>
);

interface SecureProjectsPageProps {
  onChatClick: () => void;
}

const SecureProjectsPage: FC<SecureProjectsPageProps> = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [heroTypingComplete, setHeroTypingComplete] = useState(false);

  // Add noindex meta tag - this page should not be indexed by search engines
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onContactClick={() => setShowContactForm(true)} />

      {/* Hero Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 bg-unimo-slate/10 text-unimo-slate rounded-full text-sm font-medium mb-6"
            >
              <span className="w-2 h-2 bg-unimo-slate rounded-full"></span>
              Secure Sites
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6">
              <Typewriter
                speed={90}
                variance={15}
                pauseDelay={700}
                onComplete={() => setHeroTypingComplete(true)}
              >
                Secure enough for the projects| you can't talk about.
              </Typewriter>
            </h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: heroTypingComplete ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              className="text-xl text-gray-600 mb-8"
            >
              On-device AI that runs completely offline. Your data never leaves the site. Real-time alerts without the cloud.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: heroTypingComplete ? 1 : 0, y: heroTypingComplete ? 0 : 10 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button onClick={() => setShowContactForm(true)} className="bg-gray-900 text-white hover:bg-gray-800">
                Discuss Your Requirements
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl font-medium text-gray-900 mb-4">The Security Paradox</h2>
            <p className="text-lg text-gray-600">
              Sensitive sites need visibility more than anyone, but cloud-connected IoT is a non-starter. Until now, that meant choosing between security and insight.
            </p>
          </div>

          <motion.div
            className="grid md:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.div variants={itemVariants} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-unimo-turquoise/30 transition-all">
              <div className="w-14 h-14 bg-unimo-purple/10 rounded-xl flex items-center justify-center mb-4">
                <SecurityIcon size={28} className="text-unimo-purple" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Cloud is Off Limits</h3>
              <p className="text-gray-600 text-sm">
                Standard IoT platforms send data externally. For secure sites, that's an automatic no. Air gaps exist for a reason.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-unimo-turquoise/30 transition-all">
              <div className="w-14 h-14 bg-unimo-orange/10 rounded-xl flex items-center justify-center mb-4">
                <AlertIcon size={28} className="text-unimo-orange" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delayed Awareness</h3>
              <p className="text-gray-600 text-sm">
                Without real-time monitoring, incidents are discovered after the fact. On secure sites, that delay can be catastrophic.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-unimo-turquoise/30 transition-all">
              <div className="w-14 h-14 bg-unimo-turquoise/10 rounded-xl flex items-center justify-center mb-4">
                <AIIcon size={28} className="text-unimo-turquoise" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No AI, No Insight</h3>
              <p className="text-gray-600 text-sm">
                Raw sensor data without analysis is just noise. But AI typically requires cloud processing. Until now.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-16 md:py-20 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl font-medium text-gray-900 mb-4">Airgapped Intelligence</h2>
            <p className="text-lg text-gray-600">
              unimo runs custom AI models entirely on-device. No internet required. No data exfiltration possible. Full capability, zero exposure.
            </p>
          </div>

          <motion.div
            className="grid md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.div variants={itemVariants} className="flex gap-4">
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <AirgapIcon size={24} className="text-gray-700" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">True Airgap Operation</h3>
                <p className="text-gray-600">
                  No internet connectivity required. Sensors communicate locally via encrypted mesh. Data stays on-site, always.
                </p>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex gap-4">
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <LocalProcessingIcon size={24} className="text-gray-700" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">On-Device AI Models</h3>
                <p className="text-gray-600">
                  Custom-trained models run on local hardware. Vision AI, anomaly detection, and natural language queries, all without cloud.
                </p>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex gap-4">
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertIcon size={24} className="text-gray-700" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Real-Time Local Alerts</h3>
                <p className="text-gray-600">
                  Instant notifications to authorised personnel via local network. Know about incidents as they happen, not hours later.
                </p>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex gap-4">
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldLockIcon size={24} className="text-gray-700" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Encrypted at Every Layer</h3>
                <p className="text-gray-600">
                  End-to-end encryption for sensor mesh, local storage, and any optional secure exports. Built for environments where security is non-negotiable.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Capability Stack */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-medium text-gray-900 mb-4"
          >
            Full Sensor Stack, Fully Offline
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-gray-600 mb-12 max-w-3xl"
          >
            Every capability we offer in connected environments works identically in airgapped mode. Same sensors. Same AI. Same answers.
          </motion.p>
          
          <motion.div
            className="grid md:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.div variants={itemVariants} className="bg-white border border-gray-200 rounded-lg p-6">
              <VisionIcon size={24} className="text-gray-700 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Vision AI</h3>
              <p className="text-gray-600 text-sm">
                RTSP camera feeds analysed locally. People counting, PPE detection, vehicle tracking, perimeter monitoring, all on-device.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white border border-gray-200 rounded-lg p-6">
              <AlertIcon size={24} className="text-gray-700 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Class 1 Sound</h3>
              <p className="text-gray-600 text-sm">
                MEMS microphone arrays for perimeter acoustics, gunshot detection, or equipment monitoring. Solar-powered, deploy anywhere.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white border border-gray-200 rounded-lg p-6">
              <PowerIcon size={24} className="text-gray-700 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Power & Environment</h3>
              <p className="text-gray-600 text-sm">
                Generator load, fuel consumption, temperature, humidity, air quality, vibration. The complete operational picture.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Target Sectors */}
      <section className="py-16 md:py-20 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-medium text-gray-900 mb-12"
          >
            Built For
          </motion.h2>
          
          <motion.div
            className="grid md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <NuclearIcon size={32} className="text-gray-700" />
                <h3 className="text-xl font-medium text-gray-900">Nuclear & Energy</h3>
              </div>
              <p className="text-gray-600 mb-4">
                SMR sites, decommissioning projects, substations. Environmental monitoring with zero external data exposure.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Radiation-area environmental baselines
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Perimeter acoustic monitoring
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Worker safety compliance evidence
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <ShieldLockIcon size={32} className="text-gray-700" />
                <h3 className="text-xl font-medium text-gray-900">Defence & Government</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Military installations, secure construction, government facilities. Full operational visibility without network exposure.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Perimeter intrusion detection
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Vehicle and personnel tracking
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Secure evidence logging
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <LocalProcessingIcon size={32} className="text-gray-700" />
                <h3 className="text-xl font-medium text-gray-900">Critical Infrastructure</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Data centres, telecommunications, utilities. Sites where network isolation is a feature, not a limitation.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Environmental condition monitoring
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Access logging and anomaly detection
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Predictive maintenance triggers
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              className="border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <AirgapIcon size={32} className="text-gray-700" />
                <h3 className="text-xl font-medium text-gray-900">Pharmaceutical & R&D</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Manufacturing facilities, research labs, cleanrooms. Environments with strict data sovereignty requirements.
              </p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Environmental compliance logging
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Controlled access monitoring
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                  Tamper-evident audit trails
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* The Difference */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-medium text-gray-900 mb-12 text-center"
          >
            Why unimo
          </motion.h2>
          
          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <motion.div variants={itemVariants} className="text-center">
              <div className="text-4xl font-medium text-gray-900 mb-2">Zero</div>
              <div className="text-gray-600">Data sent externally. Ever.</div>
            </motion.div>
            <motion.div variants={itemVariants} className="text-center">
              <div className="text-4xl font-medium text-gray-900 mb-2">Local</div>
              <div className="text-gray-600">AI processing. On-device models.</div>
            </motion.div>
            <motion.div variants={itemVariants} className="text-center">
              <div className="text-4xl font-medium text-gray-900 mb-2">Real-time</div>
              <div className="text-gray-600">Alerts. When they matter.</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-medium mb-4"
          >
            Let's discuss your security requirements
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl text-gray-300 mb-8"
          >
            Our team includes former defence and critical infrastructure specialists who understand what "secure" actually means.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <button 
              onClick={() => setShowContactForm(true)} 
              className="px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start the Conversation
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-gray-900 font-medium">unimo</span>
              <span className="text-gray-400 text-sm">© 2026</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-600">
              <a href="/privacy" className="hover:text-gray-900">Privacy</a>
              <button onClick={() => setShowContactForm(true)} className="hover:text-gray-900">Contact</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && <ContactForm onClose={() => setShowContactForm(false)} />}
    </div>
  );
};

export default SecureProjectsPage;
